import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, filter, Subscription } from 'rxjs';

import { ControlValueAccessorModel, getProvider } from '../../../Forms/models/control-value-accessor.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { TopicsService } from '../../../../Monitor/Core/services/topics.service';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';

import { BrandTopic } from '../../../../api/brandIdentity/models/brand-identity.api.model';
import { MentionSearchListItemModel } from '../../../../Monitor/Shared/models/mention-search-list-item.model';

interface TopicChip {
    label: string;
    topicId: number;
    favorite: boolean;
}

@Component({
    selector: 'agpr-brand-topics',
    templateUrl: './brand-topics.component.html',
    styleUrls: ['./brand-topics.component.scss'],
    providers: [getProvider(BrandTopicsComponent)],
    standalone: false
})
export class BrandTopicsComponent extends ControlValueAccessorModel<BrandTopic[]> implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    @Input() public id: string = 'brandTopics';
    @Input() public showHiddenOption: boolean = false;
    @Input() public disabled: boolean = false;
    @Input() public maxLimit: number = 10;

    private subscriptions: Subscription[] = [];
    protected suggestions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);

    topics: MentionSearchListItemModel[] = [];
    options: DropdownOptionModel[] = [];
    topicChips: TopicChip[] = [];

    constructor(
        public topicsService: TopicsService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.topicsService.getTopics()
                .pipe(filter(x => !!x))
                .subscribe(topics => {
                    this.topics = topics;
                    this.options = this.topics.map(x => new DropdownOptionModel(x.name, x));
                    this.suggestions$.next(this.options);
                    this.populateChips();
                })
        );
    }

    private toChip(topic: BrandTopic): TopicChip {
        const option = this.options.find(x => x.value.id === topic.topicId);
        return option ? {
            topicId: topic.topicId,
            label: option.label,
            favorite: !!topic.favorite
        } : null;
    }

    private populateChips() {
        this.topicChips = this.value
            .map(topic => this.toChip(topic))
            .filter( x => !!x);
    }

    selectTopic(item: DropdownOptionModel) {
        const topicId = item.value.id;

        if (this.value.some(x => x.topicId === topicId)) {
            return;
        }

        const newTopic: BrandTopic = {
            topicId,
            favorite: false
        };

        this.value = [...this.value, newTopic];
        this.topicChips = [...this.topicChips, this.toChip(newTopic)];
        this.emitValue();
    }

    removeChip(chip: TopicChip) {
        this.value = this.value.filter(x => x.topicId !== chip.topicId);
        this.topicChips = this.topicChips.filter(x => x.topicId !== chip.topicId);
        this.emitValue();
    }

    setFavorite(chip: TopicChip) {
        if (chip.favorite) {
            chip.favorite = false;
            const topic = this.value.find(x => x.topicId === chip.topicId);
            if (topic) {
                topic.favorite = false;
            }
        } else {
            this.value = this.value.map(x => ({
                ...x,
                favorite: x.topicId === chip.topicId
            }));

            this.topicChips = this.topicChips.map(x => ({
                ...x,
                favorite: x.topicId === chip.topicId
            }));
        }
        this.emitValue();
    }

    emitValue() {
        this.onModelChange(this.value);
        this.onModelTouched();
    }

    override writeValue(value: BrandTopic[] | null): void {
        this.value = value ?? [];
        this.populateChips();
    }

    override setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
